import { useState, useEffect, useCallback } from "react";
import { fetchEntertainmentEvents } from "src/api/payload/entertainment";
import { isPayloadEntertainmentSource } from "src/utils/entertainmentSource";

/**
 * Loads entertainment hub cards from Payload CMS when REACT_APP_ENTERTAINMENT_SOURCE=payload.
 *
 * @param {string} locale - `en` | `fr`
 * @returns {{
 *   data: import('src/utils/mapEntertainment').EntertainmentCard[],
 *   loading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>,
 *   isPayloadSource: boolean,
 * }}
 */
export function useEntertainmentHub(locale) {
  const isPayloadSource = isPayloadEntertainmentSource();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(isPayloadSource);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!isPayloadSource) return;

    setLoading(true);
    setError(null);

    try {
      const cards = await fetchEntertainmentEvents(locale);
      setData(cards);
    } catch (err) {
      console.error("Error fetching entertainment from Payload:", err);
      setError(err?.message || "Failed to load entertainment events");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [locale, isPayloadSource]);

  useEffect(() => {
    if (!isPayloadSource) return;
    refetch();
  }, [isPayloadSource, refetch]);

  return {
    data,
    loading,
    error,
    refetch,
    isPayloadSource,
  };
}
